import { FileText, CalendarCheck, RefreshCcw, Bell, Coins, BarChart2 } from 'lucide-react';
import { Pill } from '../components/ui/pill.js';

/**
 * /contracts — placeholder for the service-contracts surface (Phase 6).
 *
 * The mockup Design/hvac_service_contracts.html lays out CRUD for contract
 * definitions, PM auto-generation, recurring billing, and renewal alerts.
 * This screen explains what's coming until the contract data model is
 * built (Spire stores the recurring SOs but contract definitions need
 * Tinsmith-side state).
 */
export function ContractsScreen() {
  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Service contracts</h1>
        <p className="text-sm text-ink-muted">
          Recurring service agreements, auto-generated PM work orders, and renewal alerts.
        </p>
      </header>

      <div
        className="rounded-lg border bg-surface p-4 flex items-start gap-3"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        <span
          aria-hidden
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: 'var(--pill-pm-bg)', color: 'var(--pill-pm-fg)' }}
        >
          <FileText className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-medium text-ink">Contracts module — Phase 6</div>
            <Pill variant="warning">Planned</Pill>
          </div>
          <div className="text-sm text-ink-muted mt-1">
            Until this lands, you can still see per-customer service history under{' '}
            <span className="font-mono text-xs">Customers → [customer] → Quotes &amp; orders</span>.
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <PanelCard
          Icon={FileText}
          title="Contract data model"
          subtitle="Tiers, terms, equipment coverage"
          bullets={[
            'Tier templates (Bronze / Silver / Gold) with per-tier inclusions',
            'Equipment coverage list — link to Spire service_equipment',
            'Term + auto-renewal flags + price escalation rules',
          ]}
        />
        <PanelCard
          Icon={CalendarCheck}
          title="PM auto-generation"
          subtitle="Materialize work orders 30 days ahead"
          bullets={[
            'iCalendar RRULE schedules per contract',
            'Worker queues PMs 30 days out as draft work orders',
            'Reassign if a tech is unavailable that day',
          ]}
        />
        <PanelCard
          Icon={RefreshCcw}
          title="Recurring billing"
          subtitle="Spire recurring SO type 'R'"
          bullets={[
            'Monthly/quarterly/annual cadence per contract',
            'Auto-creates Spire recurring sales orders',
            'Pause / resume + proration on contract changes',
          ]}
        />
        <PanelCard
          Icon={Bell}
          title="Renewal alerts"
          subtitle="T-60, T-30, T-7 day touchpoints"
          bullets={[
            'Office dashboard banner for upcoming renewals',
            'Auto-email / SMS the customer at each threshold',
            'ARR-at-risk reporting on the office dashboard',
          ]}
        />
        <PanelCard
          Icon={Coins}
          title="Contract financials"
          subtitle="Realized vs. forecast ARR"
          bullets={[
            'Per-tier ARR roll-ups',
            'Discount + concession tracking',
            'Margin per service visit (parts + labor)',
          ]}
        />
        <PanelCard
          Icon={BarChart2}
          title="Compliance reporting"
          subtitle="PM completion % per contract"
          bullets={[
            'Missed-visit detection + dispatcher alert',
            'Customer-facing compliance score',
            'Used by office dashboard "PM compliance" KPI',
          ]}
        />
      </div>
    </div>
  );
}

function PanelCard({
  Icon,
  title,
  subtitle,
  bullets,
}: {
  Icon: typeof FileText;
  title: string;
  subtitle: string;
  bullets: ReadonlyArray<string>;
}) {
  // Planned scope — communicate non-interactive at a glance: dashed border,
  // muted text, no hover affordance, cursor stays default.
  return (
    <div
      className="rounded-lg border-2 border-dashed p-4 cursor-default select-none"
      style={{
        borderColor: 'var(--color-border-tertiary)',
        backgroundColor: 'var(--color-background-secondary)',
      }}
      aria-disabled="true"
    >
      <div className="flex items-start gap-3 opacity-80">
        <span
          aria-hidden
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-fg)' }}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink-muted">{title}</div>
          <div className="text-sm text-ink-subtle">{subtitle}</div>
        </div>
      </div>
      <ul className="mt-3 space-y-1 text-xs text-ink-subtle list-disc pl-5 opacity-90">
        {bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
    </div>
  );
}
